import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, forwardRef } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';

import { FinanceCurrencyRecord } from '../../../core/services/finance.service';
import { PartnerReferenceOptions } from '../../../core/services/partner-reference-options';

export type MoneyInputValue = [number, string];

@Component({
  selector: 'app-money-input',
  standalone: false,
  templateUrl: './money-input.html',
  styleUrl: './money-input.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MoneyInput),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => MoneyInput),
      multi: true,
    },
  ],
})
export class MoneyInput implements OnInit, OnChanges, ControlValueAccessor, Validator {
  @Input() currencies: FinanceCurrencyRecord[] = [];
  @Input() placeholder = '0.00';
  @Input() disabled = false;
  @Input() showCurrency = false;
  @Input() currencyReadonly = false;
  @Input() inputClass = 'form-control';
  @Input() selectClass = 'form-select';

  @Output() currenciesLoaded = new EventEmitter<FinanceCurrencyRecord[]>();

  amount: number | null = null;
  displayAmount = '';
  currencyIso = '';
  currencyOptions: FinanceCurrencyRecord[] = [];

  private internalDisabled = false;
  private defaultCurrencyIso = '';
  private preloadError = '';

  constructor(private readonly referenceOptions: PartnerReferenceOptions) {}

  async ngOnInit(): Promise<void> {
    await this.loadCurrencies();
    if (!this.currencyIso) {
      this.currencyIso = this.defaultCurrencyIso;
      this.emitChange();
    }
    this.onValidatorChange();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['currencies'] || this.currencies.length === 0) {
      return;
    }

    this.currencyOptions = this.normalizeCurrencyOptions(this.currencies);
    this.defaultCurrencyIso = this.resolveDefaultCurrencyIso(this.currencyOptions);
    this.ensureCurrencyOption(this.currencyIso);
    this.onValidatorChange();
  }

  get isDisabled(): boolean {
    return this.disabled || this.internalDisabled;
  }

  get isCurrencyDisabled(): boolean {
    return this.isDisabled || this.currencyReadonly;
  }

  onAmountInput(value: string): void {
    const normalized = this.normalizeAmountText(value);
    const parsed = Number(normalized);
    this.amount = Number.isFinite(parsed) ? Math.round(parsed * 100) / 100 : null;
    this.displayAmount = this.formatAmountText(normalized);
    this.emitChange();
  }

  onCurrencyChange(value: string): void {
    this.currencyIso = this.normalizeCurrency(value) || this.defaultCurrencyIso;
    this.ensureCurrencyOption(this.currencyIso);
    this.emitChange();
    this.onValidatorChange();
  }

  markTouched(): void {
    this.onTouched();
  }

  writeValue(value: unknown): void {
    this.preloadError = '';

    if (Array.isArray(value)) {
      const parsedAmount = Number(value[0]);
      this.amount = Number.isFinite(parsedAmount) ? Math.round(parsedAmount * 100) / 100 : null;
      this.displayAmount = this.formatAmount(this.amount);
      const currency = this.normalizeCurrency(value[1]);
      if (value[1] && !currency) {
        this.preloadError = 'Unable to preload money currency.';
      }
      this.currencyIso = currency || this.currencyIso || this.defaultCurrencyIso;
      this.ensureCurrencyOption(this.currencyIso);
      return;
    }

    if (typeof value === 'number' || typeof value === 'string') {
      const parsedAmount = Number(value);
      this.amount = Number.isFinite(parsedAmount) ? Math.round(parsedAmount * 100) / 100 : null;
      this.displayAmount = this.formatAmount(this.amount);
    }

    this.ensureCurrencyOption(this.currencyIso);
  }

  registerOnChange(fn: (value: MoneyInputValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.internalDisabled = isDisabled;
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    if (this.preloadError) {
      return {
        moneyPreload: {
          message: this.preloadError,
        },
      };
    }

    if (!this.normalizeCurrency(this.currencyIso)) {
      return {
        moneyCurrency: {
          message: 'Select a valid ISO currency.',
        },
      };
    }

    return null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  private async loadCurrencies(): Promise<void> {
    if (this.currencies.length > 0) {
      this.currencyOptions = this.normalizeCurrencyOptions(this.currencies);
      this.defaultCurrencyIso = this.resolveDefaultCurrencyIso(this.currencyOptions);
      this.ensureCurrencyOption(this.currencyIso);
      return;
    }

    try {
      const currencyOptions = await this.referenceOptions.options('currencies');
      const currencies = currencyOptions.map((currency) => ({
        id: currency.value,
        code: currency.code || '',
        name: currency.label,
        symbol: currency.symbol || null,
        company_base_currency: currency.is_base === true,
        active: true,
      }));
      const normalizedCurrencies = this.normalizeCurrencyOptions(currencies);
      this.currencyOptions = normalizedCurrencies;
      this.defaultCurrencyIso = this.resolveDefaultCurrencyIso(normalizedCurrencies);
      this.ensureCurrencyOption(this.defaultCurrencyIso);
      this.ensureCurrencyOption(this.currencyIso);
      this.currenciesLoaded.emit(this.currencyOptions);
    } catch {
      this.ensureCurrencyOption(this.currencyIso);
    }
  }

  private normalizeCurrencyOptions(currencies: FinanceCurrencyRecord[]): FinanceCurrencyRecord[] {
    const byCode = new Map<string, FinanceCurrencyRecord>();
    for (const currency of currencies) {
      const code = this.normalizeCurrency(currency?.code);
      if (!code) {
        continue;
      }

      byCode.set(code, {
        ...currency,
        id: currency.id || code,
        code,
        name: currency.name || code,
        active: currency.active !== false,
      });
    }

    return Array.from(byCode.values()).sort((left, right) => left.code.localeCompare(right.code));
  }

  private resolveDefaultCurrencyIso(currencies: FinanceCurrencyRecord[]): string {
    return currencies.find((currency) => currency.company_base_currency)?.code
      || currencies.find((currency) => currency.active !== false)?.code
      || currencies[0]?.code
      || '';
  }

  private ensureCurrencyOption(value: unknown): void {
    const code = this.normalizeCurrency(value);
    if (!code || this.currencyOptions.some((currency) => currency.code === code)) {
      return;
    }

    this.currencyOptions = [
      ...this.currencyOptions,
      {
        id: code,
        code,
        name: code,
        symbol: null,
        company_base_currency: false,
        active: false,
      },
    ].sort((left, right) => left.code.localeCompare(right.code));
  }

  private emitChange(): void {
    this.onChange([Number(this.amount ?? 0), this.normalizeCurrency(this.currencyIso) || this.defaultCurrencyIso]);
  }

  private normalizeAmountText(value: unknown): string {
    const raw = String(value ?? '').replace(/,/g, '').replace(/[^\d.-]/g, '');
    const negative = raw.startsWith('-') ? '-' : '';
    const unsigned = raw.replace(/-/g, '');
    const [integer = '', ...fractionParts] = unsigned.split('.');
    const fraction = fractionParts.join('').slice(0, 2);
    const hasDecimal = unsigned.includes('.');
    return `${negative}${integer || (hasDecimal ? '0' : '')}${hasDecimal ? `.${fraction}` : ''}`;
  }

  private formatAmountText(value: string): string {
    if (!value || value === '-' || value === '.' || value === '-.') {
      return value;
    }

    const negative = value.startsWith('-') ? '-' : '';
    const unsigned = value.replace(/^-/, '');
    const [integer = '0', fraction] = unsigned.split('.');
    const grouped = Number(integer || 0).toLocaleString('en-US', {
      maximumFractionDigits: 0,
      useGrouping: true,
    });
    return `${negative}${grouped}${fraction === undefined ? '' : `.${fraction}`}`;
  }

  private formatAmount(value: number | null): string {
    if (value === null || !Number.isFinite(value)) {
      return '';
    }
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
      useGrouping: true,
    });
  }

  private normalizeCurrency(value: unknown): string {
    const normalized = String(value ?? '').trim().toUpperCase();
    return /^[A-Z]{3}$/.test(normalized) ? normalized : '';
  }

  private onChange: (value: MoneyInputValue) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidatorChange: () => void = () => {};
}
